import { useRef, useState } from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { primary_color } from '../../../Helper/uiHelper'
import { getCookie } from '../../../Helper/cookieHelper'
import { useDispatch } from 'react-redux'
import { setNotification } from '../../../Redux/Reducers/notificationHandling'

/**
 * School Years → Codes: import overwrite via spreadsheet upload
 * (.csv / .xls / .xlsx) to automatic-codes/import-year-codes
 */
const ImportYearCodesButton = ({ disabled }) => {
  const dispatch = useDispatch()
  const fileRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [file, setFile] = useState(null)

  const formatMessage = (message) => {
    if (!message) return 'Import failed'
    if (typeof message === 'string') return message
    if (typeof message === 'object') return Object.values(message).flat().join(' ')
    return 'Import failed'
  }

  const reset = () => {
    setFile(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const run = () => {
    if (!file) {
      dispatch(setNotification({ type: 'error', message: 'Please choose a spreadsheet (.csv, .xls, or .xlsx)' }))
      return
    }
    const ext = (file.name.split('.').pop() || '').toLowerCase()
    if (!['csv', 'xls', 'xlsx'].includes(ext)) {
      dispatch(setNotification({ type: 'error', message: 'File must be .csv, .xls, or .xlsx' }))
      return
    }

    setLoading(true)
    const formData = new FormData()
    formData.append('file', file)
    const token = getCookie('AUTH_TOKEN')

    fetch(`${process.env.REACT_APP_API_URL}/automatic-codes/import-year-codes`, {
      method: 'POST',
      body: formData,
      headers: {
        authorization: token && token !== 'undefined' ? `Bearer ${token}` : 'Bearer token',
        Accept: 'application/json',
      },
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}))
        setLoading(false)
        if (response.ok && data?.success !== false) {
          const errCount = Array.isArray(data?.errors) ? data.errors.length : 0
          dispatch(
            setNotification({
              type: errCount ? 'warning' : 'success',
              message:
                data?.message ||
                `Updated ${data?.updated || 0} lockers${errCount ? ` (${errCount} row warnings)` : ''}`,
            })
          )
          setOpen(false)
          reset()
        } else {
          dispatch(setNotification({ type: 'error', message: formatMessage(data?.message) }))
        }
      })
      .catch(() => {
        setLoading(false)
        dispatch(setNotification({ type: 'error', message: 'Import failed' }))
      })
  }

  return (
    <>
      <Button
        className='subtitle1 mb-2'
        disabled={disabled}
        style={{ background: primary_color, border: 'none' }}
        onClick={() => setOpen(true)}>
        Import Codes
      </Button>
      <Modal
        isOpen={open}
        toggle={() => {
          if (!loading) {
            setOpen(false)
            reset()
          }
        }}
        centered
        style={{ boxShadow: 'none' }}>
        <ModalHeader
          toggle={() => {
            if (!loading) {
              setOpen(false)
              reset()
            }
          }}>
          Import Locker Codes
        </ModalHeader>
        <ModalBody>
          <div className='subtitle1 mb-3' style={{ lineHeight: 1.5 }}>
            <p className='mb-2'>Search for the lockers you want to update.</p>
            <p className='mb-2'>Click Export Data to download the spreadsheet template.</p>
            <p className='mb-2'>
              Or download a{' '}
              <a
                href={`${process.env.PUBLIC_URL || ''}/templates/locker-codes-import-sample.xlsx`}
                download='locker-codes-import-sample.xlsx'>
                sample template
              </a>{' '}
              (headers and example rows only — replace with real locker_id values before importing).
            </p>
            <p className='mb-2'>Make the required changes in the spreadsheet.</p>
            <p className='mb-2'>Import the updated spreadsheet.</p>
            <p className='mb-2'>
              <strong>Important:</strong> Do not change locker_id — this is used to identify the correct locker.
            </p>
            <p className='mb-2'>School, Block and Locker Number are for reference only.</p>
            <p className='mb-2'>Manual lockers: change the Code.</p>
            <p className='mb-2'>Automatic lockers: change the Lock ID and Sequence.</p>
            <p className='mb-3'>
              Be careful: importing the spreadsheet will directly overwrite the existing locker data.
            </p>
          </div>
          <input
            ref={fileRef}
            type='file'
            accept='.csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv'
            className='form-control-file'
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          {file ? <div className='mt-2 subtitle1 text-muted'>Selected: {file.name}</div> : null}
        </ModalBody>
        <ModalFooter>
          <Button
            color='secondary'
            disabled={loading}
            onClick={() => {
              setOpen(false)
              reset()
            }}>
            Cancel
          </Button>
          <Button color='primary' style={{ background: primary_color, border: 'none' }} disabled={loading || !file} onClick={run}>
            {loading ? 'Importing…' : 'Import'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default ImportYearCodesButton
